import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    IconButton,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    InputAdornment,
    useMediaQuery,
    useTheme,
    Tabs,
    Tab,
    RadioGroup,
    Radio,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Checkbox,
    Paper,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import NotesIcon from '@mui/icons-material/Notes';
import ImputableSearch from '../ImputableSearch';
import { Imputable } from '../../services/imputableService';
import { formatCurrency } from '../../utils/formatters';
import tesoreriaService, { GrupoCajaChica, MedioTransferencia, Banco, MarcaTarjeta, ChequeCartera } from '../../services/tesoreriaService';

interface FormularioData {
    nroSuc: number;
    fechaEmision: string;
    codigoEntidad: number;
    descripcion: string;
    aclaracion: string;
    // tipoMovimiento se envía como código STRING al endpoint /api/TesoreriaCaja/movimientos-caja.
    // Para Movimientos Varios:
    //   "5"  -> Ingreso Varios
    //   "7"  -> Egreso Varios
    // Para Movimientos Bancarios:
    //   "55" -> Depósito
    //   "56" -> Extracción
    tipoMovimiento: string;
}

interface TipoPagoData {
    tipoPago: string; // "Cheque" o "Tarjeta"
    importe: number;
    detalle: string;
    mcp?: string;
    grupoMcp?: number;
    // Campos para Cheques
    nroCarga?: number; // cheque de cartera
    banco?: string;
    nroBanco?: number;
    nroSucursal?: number;
    nroCheque?: string;
    fechaEmision?: string;
    fechaVencimiento?: string;
    ctaBancaria?: string;
    librador?: string;
    nroLibrador?: string;
    concepto?: string;
    // Campos para Tarjetas
    codigoMarca?: number;
    nroTarjeta?: string;
    nroComprobanteTa?: number;
    fecha?: string;
    fechaPresentar?: string;
    numeroroAutorizacion?: string;
    telefono?: string;
    titularTarjeta?: string;
}

interface Sucursal {
    nroSuc: number;
    nombreSucursal: string;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
};

const initialPago: TipoPagoData = {
    tipoPago: '',
    importe: 0,
    detalle: '',
    fechaEmision: new Date().toISOString().split('T')[0],
    fechaVencimiento: new Date().toISOString().split('T')[0],
    fecha: new Date().toISOString().split('T')[0],
    fechaPresentar: new Date().toISOString().split('T')[0],
};

const NuevoMovimientoCaja: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const loggedUsername = user?.username || localStorage.getItem('userName') || localStorage.getItem('username') || localStorage.getItem('user') || 'Sistema';

    // Determinar el contexto de uso del formulario:
    // - "varios"   (default): se usa desde Movimientos Varios -> códigos 5/7, etiquetas Ingreso/Egreso
    // - "bancario": se usa desde Movimientos Bancarios        -> códigos 55/56, etiquetas Depósito/Extracción
    const navigationState = (location.state || {}) as { origen?: string } | undefined;
    const origenMovimiento = navigationState?.origen === 'bancario' ? 'bancario' : 'varios';

    const COD_INGRESO = origenMovimiento === 'bancario' ? '55' : '5';
    const COD_EGRESO = origenMovimiento === 'bancario' ? '56' : '7';
    const LABEL_INGRESO = origenMovimiento === 'bancario' ? 'Depósito' : 'Ingreso';
    const LABEL_EGRESO = origenMovimiento === 'bancario' ? 'Extracción' : 'Egreso';

    const [formulario, setFormulario] = useState<FormularioData>({
        nroSuc: Number(localStorage.getItem('nroSuc') || localStorage.getItem('sucursal') || 0) || 0,
        fechaEmision: new Date().toISOString().split('T')[0],
        codigoEntidad: 0,
        descripcion: '',
        aclaracion: '',
        // Se definirá luego de la selección inicial (Ingreso/Egreso o Depósito/Extracción)
        tipoMovimiento: '',
    });

    // Paso previo: selección inicial de tipo de movimiento antes de mostrar el resto del formulario
    const [tipoMovimientoInicial, setTipoMovimientoInicial] = useState<string>('');
    const [tipoMovimientoConfirmado, setTipoMovimientoConfirmado] = useState(false);

    const [detallesPago, setDetallesPago] = useState<TipoPagoData[]>([]);
    const [openPagoDialog, setOpenPagoDialog] = useState(false);
    const [pagoActual, setPagoActual] = useState<TipoPagoData>(initialPago);
    const [tipoPagoSeleccionado, setTipoPagoSeleccionado] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const [openImputableSearch, setOpenImputableSearch] = useState(false);
    const [imputableSeleccionado, setImputableSeleccionado] = useState<Imputable | null>(null);
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [loadingSucursales, setLoadingSucursales] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    // Selección de Grupo de Caja Chica
    const [openGrupoCajaDialog, setOpenGrupoCajaDialog] = useState(false);
    const [gruposCaja, setGruposCaja] = useState<GrupoCajaChica[]>([]);
    const [grupoCajaLoading, setGrupoCajaLoading] = useState(false);
    const [grupoCajaSeleccionado, setGrupoCajaSeleccionado] = useState<GrupoCajaChica | null>(null);
    const [mediosTransferencia, setMediosTransferencia] = useState<MedioTransferencia[]>([]);
    const [cuentaTransferSeleccionadaId, setCuentaTransferSeleccionadaId] = useState<string | null>(null);
    const [mediosEfectivo, setMediosEfectivo] = useState<MedioTransferencia[]>([]);
    const [efectivoSeleccionadoId, setEfectivoSeleccionadoId] = useState<number | null>(null);
    const [bancos, setBancos] = useState<Banco[]>([]);
    const [bancoSeleccionadoId, setBancoSeleccionadoId] = useState<number | null>(null);
    // Banco seleccionado específicamente para depósitos/extracciones bancarios (55/56)
    const [bancoDepositoSeleccionado, setBancoDepositoSeleccionado] = useState<Banco | null>(null);
    const [openBancoDialog, setOpenBancoDialog] = useState(false);
    // Si es 'codigoEntidad', el diálogo se abrió para elegir banco de cartera (55/56): mostrar solo NroBanco >= 1000
    const [openBancoDialogPara, setOpenBancoDialogPara] = useState<'codigoEntidad' | 'cheque' | null>(null);
    const [bancoSearch, setBancoSearch] = useState('');
    const [bancoPage, setBancoPage] = useState(1);
    const [bancoHasMore, setBancoHasMore] = useState(true);
    const [bancoLoading, setBancoLoading] = useState(false);
    const [mediosCheques, setMediosCheques] = useState<MedioTransferencia[]>([]);
    const [chequeSeleccionadoIdx, setChequeSeleccionadoIdx] = useState<string | null>(null);
    // Cheques de cartera (solo Egreso + Cheques de Tercero)
    const [chequesCartera, setChequesCartera] = useState<ChequeCartera[]>([]);
    const [chequesCarteraSeleccionados, setChequesCarteraSeleccionados] = useState<Set<number>>(new Set());
    const [loadingChequesCartera, setLoadingChequesCartera] = useState(false);
    const [esChequeCartera, setEsChequeCartera] = useState(false);
    const [busquedaCheque, setBusquedaCheque] = useState('');
    const [orderBy, setOrderBy] = useState<keyof ChequeCartera | ''>('');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [mediosTarjeta, setMediosTarjeta] = useState<MedioTransferencia[]>([]);
    const [tarjetaSeleccionadaIdx, setTarjetaSeleccionadaIdx] = useState<string | null>(null);
    const [marcasTarjetas, setMarcasTarjetas] = useState<MarcaTarjeta[]>([]);
    // Almacenar los objetos completos de medios de tarjeta desde la API
    const [mediosTarjetaCompletos, setMediosTarjetaCompletos] = useState<any[]>([]);
    const [mediosOtros, setMediosOtros] = useState<MedioTransferencia[]>([]);
    const [otrosSeleccionadoIdx, setOtrosSeleccionadoIdx] = useState<string | null>(null);
    const [isMontoFocused, setIsMontoFocused] = useState(false);

    const isTabletOrLarger = useMediaQuery(theme.breakpoints.up('md'));

    const handleRequestSort = (property: keyof ChequeCartera) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const ordenarCheques = (cheques: ChequeCartera[]): ChequeCartera[] => {
        if (!orderBy) return cheques;
        return [...cheques].sort((a, b) => {
            let aValue: any = a[orderBy];
            let bValue: any = b[orderBy];
            if (aValue == null) aValue = '';
            if (bValue == null) bValue = '';
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return order === 'asc' ? aValue - bValue : bValue - aValue;
            }
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            if (order === 'asc') return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
            return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
        });
    };

    const handleSeleccionarTodos = (chequesFiltrados: ChequeCartera[]) => {
        const todosSeleccionados = chequesFiltrados.every(cheque => chequesCarteraSeleccionados.has(cheque.nroCarga));
        const nuevoSet = new Set(chequesCarteraSeleccionados);
        if (todosSeleccionados) {
            chequesFiltrados.forEach(cheque => nuevoSet.delete(cheque.nroCarga));
        } else {
            chequesFiltrados.forEach(cheque => nuevoSet.add(cheque.nroCarga));
        }
        setChequesCarteraSeleccionados(nuevoSet);
    };

    const totalImporteSeleccionado = useMemo(() => {
        return chequesCartera
            .filter(cheque => chequesCarteraSeleccionados.has(cheque.nroCarga))
            .reduce((total, cheque) => total + (cheque.importe || 0), 0);
    }, [chequesCartera, chequesCarteraSeleccionados]);

    // Cargar sucursales desde /api/SucursalesVenta
    useEffect(() => {
        const cargarSucursales = async () => {
            setLoadingSucursales(true);
            try {
                const response = await api.get('/api/SucursalesVenta');
                const data = response.data;

                // La respuesta puede venir como { items: [...] } o como array directo
                if (data && Array.isArray((data as any).items)) {
                    setSucursales((data as any).items);
                } else if (Array.isArray(data)) {
                    setSucursales(data);
                } else {
                    setSucursales([]);
                }
            } catch (error: any) {
                console.error('Error al cargar sucursales de venta:', error);
                enqueueSnackbar('Error al cargar sucursales', { variant: 'error' });
                setSucursales([]);
            } finally {
                setLoadingSucursales(false);
            }
        };
        cargarSucursales();
    }, [enqueueSnackbar]);

    // Cargar grupos de caja
    useEffect(() => {
        const cargarGruposCaja = async () => {
            setGrupoCajaLoading(true);
            try {
                const grupos = await tesoreriaService.getGruposCajaChica({ usuario: loggedUsername });
                setGruposCaja(grupos);
            } catch (error: any) {
                console.error('Error al cargar grupos de caja:', error);
                enqueueSnackbar('Error al cargar grupos de caja', { variant: 'error' });
            } finally {
                setGrupoCajaLoading(false);
            }
        };
        cargarGruposCaja();
    }, [loggedUsername, enqueueSnackbar]);

    // Cargar bancos en diálogo al abrir o al buscar (igual que en carga de recibos)
    const BANCO_PAGE_SIZE = 20; // Límite del backend (1-20)
    useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;

        const fetchPage = async (reset: boolean) => {
            try {
                setBancoLoading(true);
                const pageToLoad = reset ? 1 : bancoPage;
                const items = await tesoreriaService.getBancosPaged({
                    searchTerm: bancoSearch || undefined,
                    pageNumber: pageToLoad,
                    pageSize: BANCO_PAGE_SIZE,
                });
                if (cancelled) return;
                setBancoHasMore(items.length >= BANCO_PAGE_SIZE);
                setBancoPage(pageToLoad + 1);
                setBancos(prev => {
                    const next = reset ? items : [...prev, ...items];
                    const map = new Map<number, Banco>();
                    for (const b of next) if (!map.has(b.id)) map.set(b.id, b);
                    return Array.from(map.values());
                });
            } catch (e) {
                if (!cancelled) {
                    console.error('No se pudieron cargar los bancos', e);
                    setBancos([]);
                }
            } finally {
                if (!cancelled) setBancoLoading(false);
            }
        };

        const fetchAllPagesForCartera = async () => {
            try {
                setBancoLoading(true);
                const mapById = new Map<number, Banco>();
                let pageNumber = 1;
                let hasMore = true;
                while (hasMore && !cancelled) {
                    const items = await tesoreriaService.getCarteraBancosPaged({
                        searchTerm: bancoSearch || undefined,
                        pageNumber,
                        pageSize: BANCO_PAGE_SIZE,
                    });
                    if (cancelled) return;
                    for (const b of items) if (!mapById.has(b.id)) mapById.set(b.id, b);
                    if (items.length < BANCO_PAGE_SIZE) hasMore = false;
                    else pageNumber += 1;
                    if (!cancelled) setBancos(Array.from(mapById.values()));
                }
                if (!cancelled) {
                    setBancoHasMore(false);
                    setBancoPage(pageNumber + 1);
                }
            } catch (e) {
                if (!cancelled) {
                    console.error('No se pudieron cargar los bancos de cartera', e);
                    setBancos([]);
                }
            } finally {
                if (!cancelled) setBancoLoading(false);
            }
        };

        if (openBancoDialog) {
            if (openBancoDialogPara === 'codigoEntidad') {
                timer = setTimeout(() => fetchAllPagesForCartera(), 300);
            } else {
                timer = setTimeout(() => fetchPage(true), 300);
            }
        }
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [openBancoDialog, openBancoDialogPara, bancoSearch]);

    const handleBancoScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
        if (!bancoHasMore || bancoLoading) return;
        const target = e.currentTarget;
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 64) {
            (async () => {
                try {
                    setBancoLoading(true);
                    const items = await tesoreriaService.getBancosPaged({
                        searchTerm: bancoSearch || undefined,
                        pageNumber: bancoPage,
                        pageSize: BANCO_PAGE_SIZE,
                    });
                    setBancoHasMore(items.length >= BANCO_PAGE_SIZE);
                    setBancoPage(prev => prev + 1);
                    setBancos(prev => {
                        const next = [...prev, ...items];
                        const map = new Map<number, Banco>();
                        for (const b of next) if (!map.has(b.id)) map.set(b.id, b);
                        return Array.from(map.values());
                    });
                } finally {
                    setBancoLoading(false);
                }
            })();
        }
    };

    // Cargar medios cuando se selecciona un grupo de caja
    useEffect(() => {
        const cargarMedios = async () => {
            if (grupoCajaSeleccionado?.codigo) {
                try {
                    const [
                        mediosChequesData,
                        mediosTransferenciaData,
                        mediosEfectivoData,
                        mediosOtrosData,
                        responseTarjeta
                    ] = await Promise.all([
                        tesoreriaService.getMediosChequesPorGrupo(grupoCajaSeleccionado.codigo),
                        tesoreriaService.getMediosTransferenciaPorGrupo(grupoCajaSeleccionado.codigo),
                        tesoreriaService.getMediosEfectivoPorGrupo(grupoCajaSeleccionado.codigo),
                        tesoreriaService.getMediosOtrosPorGrupo(grupoCajaSeleccionado.codigo),
                        api.get<any>(`/api/Mcps/por-grupo/${grupoCajaSeleccionado.codigo}`, {
                            headers: {
                                Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : undefined,
                            },
                            params: { pageNumber: 1, pageSize: 1000 },
                        }),
                    ]);
                    
                    setMediosCheques(mediosChequesData);
                    setMediosTransferencia(mediosTransferenciaData);
                    setMediosEfectivo(mediosEfectivoData);
                    setMediosOtros(mediosOtrosData);
                    
                    // Procesar medios de tarjeta
                    const data = responseTarjeta.data;
                    const items: any[] = Array.isArray(data)
                        ? data
                        : Array.isArray(data?.items)
                        ? data.items
                        : Array.isArray(data?.value)
                        ? data.value
                        : [];

                    const filtrados = items.filter((it: any) => {
                        const rawTipo = ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase();
                        const rawClase = ((it?.clase ?? it?.Clase ?? '') + '').trim().toUpperCase();
                        const rawNombre = ((it?.nombre ?? it?.banco ?? it?.descripcion ?? it?.detalle ?? '') + '')
                            .trim()
                            .toUpperCase();
                        const looksLikeTarjeta =
                            rawTipo === 'T' ||
                            rawTipo === 'TA' ||
                            rawTipo === 'TJ' ||
                            rawTipo === 'TC' ||
                            rawTipo === 'TD' ||
                            rawTipo.startsWith('TARJ') ||
                            rawClase.includes('TARJ') ||
                            rawNombre.includes('TARJ');
                        return looksLikeTarjeta;
                    });

                    // Guardar objetos completos (aunque no los usaremos para tarjetas, igual que en NuevaOrdenPago)
                    setMediosTarjetaCompletos(filtrados);
                    
                    // Mapear a MedioTransferencia para la UI
                    const mediosTarjetaData = filtrados.map((it: any, idx: number) => ({
                        id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
                        nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
                        descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
                        mcp: it?.mcp ?? it?.MCP ?? undefined,
                        clase: it?.clase ?? it?.Clase ?? undefined,
                        grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
                        nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
                        tipo: it?.tipo ?? it?.Tipo ?? undefined,
                    }));
                    setMediosTarjeta(mediosTarjetaData);
                } catch (error: any) {
                    console.error('Error al cargar medios:', error);
                    setMediosCheques([]);
                    setMediosTransferencia([]);
                    setMediosEfectivo([]);
                    setMediosOtros([]);
                    setMediosTarjeta([]);
                    setMediosTarjetaCompletos([]);
                }
            } else {
                setMediosCheques([]);
                setMediosTransferencia([]);
                setMediosEfectivo([]);
                setMediosOtros([]);
                setMediosTarjeta([]);
                setMediosTarjetaCompletos([]);
            }
        };
        cargarMedios();
    }, [grupoCajaSeleccionado]);

    // Cargar marcas de tarjeta al seleccionar tipo Tarjeta en el diálogo de pago
    useEffect(() => {
        if (openPagoDialog && tipoPagoSeleccionado === 'Tarjeta') {
            tesoreriaService.getMarcasTarjetas()
                .then((marcas) => setMarcasTarjetas(marcas || []))
                .catch(() => setMarcasTarjetas([]));
        } else {
            setMarcasTarjetas([]);
        }
    }, [openPagoDialog, tipoPagoSeleccionado]);

    // Cargar cheques de cartera cuando es Egreso (código COD_EGRESO)
    // + Cheques de Tercero y hay medio específico seleccionado
    useEffect(() => {
        if (tipoMovimientoInicial === COD_EGRESO && tipoPagoSeleccionado === 'Cheque' && chequeSeleccionadoIdx != null && openPagoDialog && grupoCajaSeleccionado) {
            setEsChequeCartera(true);
            setLoadingChequesCartera(true);
            const numeroGrupo = grupoCajaSeleccionado.codigo ?? grupoCajaSeleccionado.id;
            if (numeroGrupo == null) {
                setLoadingChequesCartera(false);
                return;
            }
            (async () => {
                try {
                    const cajaAbierta = await tesoreriaService.getCajaAbiertaPorGrupo(numeroGrupo);
                    const codigoCaja = cajaAbierta?.codigoCaja ?? cajaAbierta?.nroCaja ?? cajaAbierta?.codigo ?? cajaAbierta?.id ?? null;
                    if (cajaAbierta && codigoCaja != null && codigoCaja > 0) {
                        const PAGE_SIZE = 20;
                        let pageNumber = 1;
                        let allCheques: ChequeCartera[] = [];
                        let hasMore = true;
                        while (hasMore) {
                            const result = await tesoreriaService.getChequesCartera(codigoCaja, pageNumber, PAGE_SIZE);
                            allCheques = [...allCheques, ...result.cheques];
                            hasMore = result.hasMore && result.cheques.length > 0;
                            if (!hasMore) break;
                            pageNumber++;
                            if (pageNumber > 100) break;
                        }
                        setChequesCartera(allCheques);
                    } else {
                        enqueueSnackbar('No se encontró una caja abierta para este grupo', { variant: 'warning' });
                        setChequesCartera([]);
                    }
                } catch (error) {
                    console.error('Error al cargar cheques de cartera:', error);
                    enqueueSnackbar('Error al cargar cheques de cartera', { variant: 'error' });
                    setChequesCartera([]);
                } finally {
                    setLoadingChequesCartera(false);
                }
            })();
        } else {
            setEsChequeCartera(false);
            setChequesCartera([]);
            setChequesCarteraSeleccionados(new Set());
        }
    }, [tipoMovimientoInicial, tipoPagoSeleccionado, chequeSeleccionadoIdx, openPagoDialog, grupoCajaSeleccionado, enqueueSnackbar, COD_EGRESO]);

    const handleFormularioChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormulario(prev => ({ ...prev, [name]: value }));
    };

    const handleSucursalChange = (e: SelectChangeEvent<number>) => {
        const value = Number(e.target.value);
        console.log('🏢 Cambio de sucursal seleccionada:', value);
        setFormulario(prev => {
            const nuevo = { ...prev, nroSuc: value };
            console.log('📝 Nuevo estado de formulario.nroSuc:', nuevo.nroSuc);
            return nuevo;
        });
    };

    const handleImputableSelect = (imputable: Imputable) => {
        setImputableSeleccionado(imputable);
        setFormulario(prev => {
            const tipoActual = (prev.tipoMovimiento || tipoMovimientoInicial || COD_INGRESO).toString();
            const esMovimientoBancario =
                origenMovimiento === 'bancario' && (tipoActual === COD_INGRESO || tipoActual === COD_EGRESO);

            return {
                ...prev,
                // En depósito (55) o extracción (56) bancaria, CodigoEntidad debe ser el NroBanco de cartera,
                // por lo que NO lo sobreescribimos con la cuenta imputable.
                codigoEntidad: esMovimientoBancario ? prev.codigoEntidad : imputable.nroCuenta,
                descripcion: imputable.nomCuenta || '',
            };
        });
        setOpenImputableSearch(false);
    };

    const handleOpenPagoDialog = () => {
        if (!imputableSeleccionado) {
            enqueueSnackbar('Debe seleccionar un Motivo primero', { variant: 'warning' });
            return;
        }
        if (!grupoCajaSeleccionado) {
            enqueueSnackbar('Debe seleccionar un Grupo de Caja primero', { variant: 'warning' });
            setOpenGrupoCajaDialog(true);
            return;
        }
        setPagoActual(initialPago);
        setTipoPagoSeleccionado('');
        setActiveStep(0);
        setOpenPagoDialog(true);
    };

    const handleClosePagoDialog = () => {
        setOpenPagoDialog(false);
        setPagoActual(initialPago);
        setTipoPagoSeleccionado('');
        setActiveStep(0);
        setBancoSeleccionadoId(null);
        setChequeSeleccionadoIdx(null);
        setEfectivoSeleccionadoId(null);
        setCuentaTransferSeleccionadaId(null);
        setTarjetaSeleccionadaIdx(null);
        setOtrosSeleccionadoIdx(null);
        setEsChequeCartera(false);
        setChequesCartera([]);
        setChequesCarteraSeleccionados(new Set());
        setBusquedaCheque('');
        setOrderBy('');
        setOrder('asc');
        setLoadingChequesCartera(false);
    };

    const handleTipoPagoChange = (e: SelectChangeEvent<string>) => {
        const tipo = e.target.value;
        setTipoPagoSeleccionado(tipo);
        setPagoActual(prev => ({ ...prev, tipoPago: tipo }));
        setActiveStep(1);
    };

    const handlePagoFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setPagoActual(prev => ({ ...prev, [name]: value }));
    };

    const handleChequeMedioChange = (e: SelectChangeEvent<string>) => {
        const idx = e.target.value;
        setChequeSeleccionadoIdx(idx);
        const medio = mediosCheques.find((_, i) => String(i) === idx);
        if (medio) {
            setPagoActual(prev => ({ ...prev, mcp: medio.mcp || '', grupoMcp: medio.grupo }));
        }
    };

    const handleAgregarPago = () => {
        if (!grupoCajaSeleccionado) {
            enqueueSnackbar('Debe seleccionar un Grupo de Caja', { variant: 'warning' });
            setOpenGrupoCajaDialog(true);
            return;
        }

        if (pagoActual.tipoPago === 'Cheque' && esChequeCartera) {
            if (chequesCarteraSeleccionados.size === 0) {
                enqueueSnackbar('Debe seleccionar al menos un cheque de cartera', { variant: 'warning' });
                return;
            }
            const chequesSeleccionados = chequesCartera.filter(c => chequesCarteraSeleccionados.has(c.nroCarga));
            const limpiarYLimitar = (texto: string | undefined, maxLength: number): string => (!texto ? '' : texto.trim().slice(0, maxLength));
            const nuevosPagos: TipoPagoData[] = chequesSeleccionados.map(cheque => {
                const fechaEmision = cheque.fechaEmision ? new Date(cheque.fechaEmision).toISOString().split('T')[0] : '';
                const fechaVencimiento = cheque.fechaVencimiento ? new Date(cheque.fechaVencimiento).toISOString().split('T')[0] : '';
                return {
                    ...pagoActual,
                    tipoPago: 'Cheque',
                    nroCarga: cheque.nroCarga,
                    nroBanco: cheque.nroBanco,
                    banco: cheque.nombreBanco,
                    nroSucursal: cheque.nroSucursal,
                    nroCheque: String(cheque.nroCheque),
                    importe: cheque.importe,
                    fechaEmision,
                    fechaVencimiento,
                    ctaBancaria: limpiarYLimitar(cheque.nroCuenta, 20),
                    librador: limpiarYLimitar(cheque.librador, 25),
                    nroLibrador: limpiarYLimitar(cheque.nroLibrador, 20),
                    concepto: limpiarYLimitar(cheque.concepto, 255),
                    grupoMcp: grupoCajaSeleccionado.codigo,
                };
            });
            setDetallesPago(prev => [...prev, ...nuevosPagos]);
            handleClosePagoDialog();
            return;
        }

        if (!pagoActual.tipoPago || !pagoActual.importe || pagoActual.importe <= 0) {
            enqueueSnackbar('Debe completar el tipo de pago y el importe', { variant: 'warning' });
            return;
        }

        // Validaciones según el tipo de pago
        if (pagoActual.tipoPago === 'Efectivo') {
            if (!pagoActual.mcp) {
                enqueueSnackbar('Debe seleccionar un medio de efectivo', { variant: 'warning' });
                return;
            }
        } else if (pagoActual.tipoPago === 'Transferencia') {
            if (!cuentaTransferSeleccionadaId || !pagoActual.mcp) {
                enqueueSnackbar('Debe seleccionar un medio de transferencia', { variant: 'warning' });
                return;
            }
        } else if (pagoActual.tipoPago === 'Cheque') {
            if (!esChequeCartera && (!pagoActual.mcp || !pagoActual.nroBanco || !pagoActual.nroSucursal || !pagoActual.nroCheque || !pagoActual.librador || !pagoActual.nroLibrador)) {
                enqueueSnackbar('Debe completar todos los campos del cheque (incluido CUIT del librador)', { variant: 'warning' });
                return;
            }
        } else if (pagoActual.tipoPago === 'Tarjeta') {
            if (mediosTarjeta.length > 0 && !tarjetaSeleccionadaIdx) {
                enqueueSnackbar('Debe seleccionar un medio de tarjeta', { variant: 'warning' });
                return;
            }
            if (!pagoActual.mcp) {
                enqueueSnackbar('Debe seleccionar un medio de cobro', { variant: 'warning' });
                return;
            }
            if (!pagoActual.codigoMarca || pagoActual.codigoMarca <= 0) {
                enqueueSnackbar('Debe seleccionar la Marca de tarjeta', { variant: 'warning' });
                return;
            }
            if (!pagoActual.nroTarjeta || String(pagoActual.nroTarjeta).trim() === '') {
                enqueueSnackbar('Debe ingresar el N° de Tarjeta', { variant: 'warning' });
                return;
            }
            const nroComp = Number(pagoActual.nroComprobanteTa);
            if (!Number.isFinite(nroComp) || nroComp <= 0) {
                enqueueSnackbar('Debe ingresar el N° Comprobante', { variant: 'warning' });
                return;
            }
            if (!pagoActual.fecha) {
                enqueueSnackbar('Debe ingresar la Fecha', { variant: 'warning' });
                return;
            }
            if (!pagoActual.numeroroAutorizacion || String(pagoActual.numeroroAutorizacion).trim() === '') {
                enqueueSnackbar('Debe ingresar el N° Autorización/Lote', { variant: 'warning' });
                return;
            }
        } else if (pagoActual.tipoPago === 'Otros') {
            if (mediosOtros.length > 0 && !otrosSeleccionadoIdx) {
                enqueueSnackbar('Debe seleccionar un medio', { variant: 'warning' });
                return;
            }
            if (!pagoActual.mcp) {
                enqueueSnackbar('Debe seleccionar un medio de cobro', { variant: 'warning' });
                return;
            }
        }

        setPagoActual(prev => ({ ...prev, grupoMcp: grupoCajaSeleccionado.codigo }));
        const importeNum = Number(pagoActual.importe) || 0;
        setDetallesPago(prev => [...prev, { ...pagoActual, importe: importeNum, grupoMcp: grupoCajaSeleccionado.codigo }]);
        handleClosePagoDialog();
    };

    const removePago = (index: number) => {
        setDetallesPago(prev => prev.filter((_, i) => i !== index));
    };

    const totalMedios = detallesPago.reduce((sum, p) => sum + (Number(p.importe) || 0), 0);

    const renderCamposPago = () => {
        switch (tipoPagoSeleccionado) {
            case 'Efectivo':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                EFECTIVO - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                    {mediosEfectivo.map((medio) => (
                                        <Grid item xs={12} key={medio.id}>
                                            <Card
                                                variant={efectivoSeleccionadoId === medio.id ? 'elevation' : 'outlined'}
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    setEfectivoSeleccionadoId(medio.id);
                                                    setPagoActual(prev => ({ ...prev, medioLabel: medio.nombre, mcp: medio.mcp || medio.nombre }));
                                                }}
                                            >
                                                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <input
                                                        type="radio"
                                                        checked={efectivoSeleccionadoId === medio.id}
                                                        onChange={() => {
                                                            setEfectivoSeleccionadoId(medio.id);
                                                            setPagoActual(prev => ({ ...prev, medioLabel: medio.nombre, mcp: medio.mcp || medio.nombre }));
                                                        }}
                                                        style={{ marginRight: 8 }}
                                                    />
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                        {medio.descripcion && (
                                                            <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                        )}
                                                        {medio.clase && (
                                                            <Typography variant="caption">{medio.clase}</Typography>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                    {mediosEfectivo.length === 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2">No hay medios de efectivo disponibles para este grupo.</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Monto"
                                name="importe"
                                type="number"
                                value={
                                    isMontoFocused
                                        ? (pagoActual.importe === 0 ? '' : pagoActual.importe)
                                        : (Number.isFinite(pagoActual.importe as any) ? Number(pagoActual.importe).toFixed(2) : '')
                                }
                                onChange={handlePagoFieldChange}
                                onFocus={() => setIsMontoFocused(true)}
                                onBlur={() => {
                                    setIsMontoFocused(false);
                                    setPagoActual(prev => ({
                                        ...prev,
                                        importe: Math.trunc((Number(prev.importe) || 0) * 100) / 100
                                    }));
                                }}
                                inputProps={{ step: '0.01', inputMode: 'decimal' }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );

            case 'Transferencia':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                TRANSFERENCIAS - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <RadioGroup
                                    name="transferOption"
                                    value={cuentaTransferSeleccionadaId != null ? String(cuentaTransferSeleccionadaId) : ''}
                                    onChange={(e) => {
                                        const newId = String(e.target.value);
                                        setCuentaTransferSeleccionadaId(newId);
                                        const idx = Number(newId);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosTransferencia.length) {
                                            const medioSel = mediosTransferencia[idx];
                                            const idNum = Number(medioSel.id);
                                            setPagoActual(prev => ({ ...prev, nroCuenta: Number.isFinite(idNum) ? idNum : undefined, medioLabel: medioSel.nombre, mcp: medioSel.mcp || medioSel.nombre }));
                                        }
                                    }}
                                >
                                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                        {mediosTransferencia.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(cuentaTransferSeleccionadaId) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosTransferencia.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios de transferencia disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Monto"
                                name="importe"
                                type="number"
                                value={isMontoFocused && pagoActual.importe === 0 ? '' : pagoActual.importe}
                                onChange={handlePagoFieldChange}
                                onFocus={() => setIsMontoFocused(true)}
                                onBlur={() => setIsMontoFocused(false)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );

            case 'Tarjeta':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                TARJETAS - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <RadioGroup
                                    name="tarjetaOption"
                                    value={tarjetaSeleccionadaIdx ?? ''}
                                    onChange={(e) => {
                                        const newIdx = String(e.target.value);
                                        setTarjetaSeleccionadaIdx(newIdx);
                                        const idx = Number(newIdx);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosTarjeta.length) {
                                            const medioSel = mediosTarjeta[idx];
                                            const idNum = Number(medioSel.id);
                                            setPagoActual(prev => ({
                                                ...prev,
                                                nroCuenta: Number.isFinite(idNum) ? idNum : undefined,
                                                medioLabel: medioSel.nombre,
                                                mcp: medioSel.mcp || medioSel.nombre,
                                            }));
                                        }
                                    }}
                                >
                                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                        {mediosTarjeta.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(tarjetaSeleccionadaIdx) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosTarjeta.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios de tarjeta disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>* Marca</InputLabel>
                                <Select
                                    name="codigoMarca"
                                    value={String(pagoActual.codigoMarca ?? '')}
                                    onChange={(e) => setPagoActual(prev => ({ ...prev, codigoMarca: Number(e.target.value) || 0 }))}
                                    label="* Marca"
                                >
                                    {marcasTarjetas.map((m, idx) => (
                                        <MenuItem key={m.id ?? idx} value={String(m.id)}>
                                            {m.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="* N° de Tarjeta"
                                name="nroTarjeta"
                                value={pagoActual.nroTarjeta || ''}
                                onChange={handlePagoFieldChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="* N° Comprobante"
                                name="nroComprobanteTa"
                                type="number"
                                value={pagoActual.nroComprobanteTa ?? ''}
                                onChange={handlePagoFieldChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="* Fecha"
                                name="fecha"
                                type="date"
                                value={pagoActual.fecha || ''}
                                onChange={handlePagoFieldChange}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Fecha Presentación"
                                name="fechaPresentar"
                                type="date"
                                value={pagoActual.fechaPresentar || ''}
                                onChange={handlePagoFieldChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="* N° Autorización/Lote"
                                name="numeroroAutorizacion"
                                value={pagoActual.numeroroAutorizacion || ''}
                                onChange={handlePagoFieldChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Teléfono"
                                name="telefono"
                                value={pagoActual.telefono || ''}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Titular"
                                name="titularTarjeta"
                                value={pagoActual.titularTarjeta || ''}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Monto"
                                name="importe"
                                type="number"
                                value={isMontoFocused && pagoActual.importe === 0 ? '' : pagoActual.importe}
                                onChange={handlePagoFieldChange}
                                onFocus={() => setIsMontoFocused(true)}
                                onBlur={() => setIsMontoFocused(false)}
                                InputProps={{
                                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
                );

            case 'Cheque':
                if (tipoMovimientoInicial === COD_EGRESO) {
                    return (
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    CHEQUES DE TERCERO - Seleccione el medio específico:
                                </Typography>
                                <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                    <RadioGroup
                                        name="chequeOption"
                                        value={chequeSeleccionadoIdx ?? ''}
                                        onChange={(e) => {
                                            const newIdx = String(e.target.value);
                                            setChequeSeleccionadoIdx(newIdx);
                                            const idx = Number(newIdx);
                                            if (!isNaN(idx) && idx >= 0 && idx < mediosCheques.length) {
                                                const medioSel = mediosCheques[idx];
                                                setPagoActual(prev => ({ ...prev, medioLabel: medioSel.nombre, mcp: medioSel.mcp || medioSel.nombre }));
                                            }
                                        }}
                                    >
                                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                            {mediosCheques.map((medio, idx) => (
                                                <Grid item xs={12} key={medio.id ?? idx}>
                                                    <Card variant={(String(chequeSeleccionadoIdx) === String(idx)) ? 'elevation' : 'outlined'} sx={{ cursor: 'pointer' }} onClick={() => { setChequeSeleccionadoIdx(String(idx)); const m = mediosCheques[idx]; setPagoActual(prev => ({ ...prev, medioLabel: m.nombre, mcp: m.mcp || m.nombre })); }}>
                                                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <FormControlLabel
                                                                value={String(idx)}
                                                                control={<Radio />}
                                                                label={
                                                                    <Box>
                                                                        <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                        {medio.descripcion && <Typography variant="caption" display="block">{medio.descripcion}</Typography>}
                                                                        {medio.clase && <Typography variant="caption">{medio.clase}</Typography>}
                                                                    </Box>
                                                                }
                                                                sx={{ m: 0 }}
                                                            />
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                            {mediosCheques.length === 0 && (
                                                <Grid item xs={12}>
                                                    <Typography variant="body2">No hay medios de cheques disponibles para este grupo.</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </RadioGroup>
                                </Box>
                            </Grid>
                            {esChequeCartera && (
                                <>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                Seleccione uno o varios cheques de cartera:
                                            </Typography>
                                            {isTabletOrLarger && chequesCartera.length > 0 && (() => {
                                                const terminoBusqueda = busquedaCheque.toLowerCase().trim();
                                                const montoBusqueda = parseFloat(terminoBusqueda.replace(/[^\d.,]/g, '').replace(',', '.'));
                                                const filtrados = terminoBusqueda ? chequesCartera.filter(cheque => {
                                                    const coincideTexto = cheque.nombreBanco?.toLowerCase().includes(terminoBusqueda) ||
                                                        String(cheque.nroCheque).includes(terminoBusqueda) ||
                                                        cheque.librador?.toLowerCase().includes(terminoBusqueda) ||
                                                        cheque.nombreSucursal?.toLowerCase().includes(terminoBusqueda) ||
                                                        cheque.concepto?.toLowerCase().includes(terminoBusqueda) ||
                                                        String(cheque.nroSucursal).includes(terminoBusqueda);
                                                    const coincideMonto = !isNaN(montoBusqueda) && Math.abs(cheque.importe - montoBusqueda) < 0.01;
                                                    return coincideTexto || coincideMonto;
                                                }) : chequesCartera;
                                                const ordenados = ordenarCheques(filtrados);
                                                const todos = ordenados.length > 0 && ordenados.every(cheque => chequesCarteraSeleccionados.has(cheque.nroCarga));
                                                return (
                                                    <Button variant="outlined" size="small" onClick={() => handleSeleccionarTodos(ordenados)} sx={{ minWidth: '140px' }}>
                                                        {todos ? 'Deseleccionar todos' : 'Seleccionar todos'}
                                                    </Button>
                                                );
                                            })()}
                                        </Box>
                                        <TextField
                                            fullWidth
                                            placeholder="Buscar por banco, número de cheque, librador, monto..."
                                            value={busquedaCheque}
                                            onChange={(e) => setBusquedaCheque(e.target.value)}
                                            size="small"
                                            sx={{ mb: 2 }}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                                        />
                                        {loadingChequesCartera ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><Typography>Cargando cheques de cartera...</Typography></Box>
                                        ) : chequesCartera.length === 0 ? (
                                            <Box sx={{ p: 2, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No hay cheques disponibles en cartera.</Typography></Box>
                                        ) : (() => {
                                            const terminoBusqueda = busquedaCheque.toLowerCase().trim();
                                            const chequesFiltrados = terminoBusqueda ? chequesCartera.filter(cheque => {
                                                const coincideTexto = cheque.nombreBanco?.toLowerCase().includes(terminoBusqueda) || String(cheque.nroCheque).includes(terminoBusqueda) || cheque.librador?.toLowerCase().includes(terminoBusqueda) || cheque.nombreSucursal?.toLowerCase().includes(terminoBusqueda) || cheque.concepto?.toLowerCase().includes(terminoBusqueda) || String(cheque.nroSucursal).includes(terminoBusqueda);
                                                const montoBusqueda = parseFloat(terminoBusqueda.replace(/[^\d.,]/g, '').replace(',', '.'));
                                                const coincideMonto = !isNaN(montoBusqueda) && Math.abs(cheque.importe - montoBusqueda) < 0.01;
                                                return coincideTexto || coincideMonto;
                                            }) : chequesCartera;
                                            const chequesOrdenados = ordenarCheques(chequesFiltrados);
                                            const todosSeleccionados = chequesOrdenados.length > 0 && chequesOrdenados.every(cheque => chequesCarteraSeleccionados.has(cheque.nroCarga));
                                            if (isTabletOrLarger) {
                                                return (
                                                    <TableContainer component={Paper} sx={{ maxHeight: { xs: '50vh', sm: 400 }, overflowY: 'auto' }}>
                                                        <Table size="small" stickyHeader>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell padding="checkbox" sx={{ width: 50 }}>
                                                                        <Checkbox indeterminate={chequesCarteraSeleccionados.size > 0 && !todosSeleccionados} checked={todosSeleccionados && chequesOrdenados.length > 0} onChange={() => handleSeleccionarTodos(chequesOrdenados)} inputProps={{ 'aria-label': 'seleccionar todos' }} />
                                                                    </TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'nombreBanco'} direction={orderBy === 'nombreBanco' ? order : 'asc'} onClick={() => handleRequestSort('nombreBanco')}>Banco</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'nroCheque'} direction={orderBy === 'nroCheque' ? order : 'asc'} onClick={() => handleRequestSort('nroCheque')}>N° Cheque</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'nombreSucursal'} direction={orderBy === 'nombreSucursal' ? order : 'asc'} onClick={() => handleRequestSort('nombreSucursal')}>Sucursal</TableSortLabel></TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'importe'} direction={orderBy === 'importe' ? order : 'asc'} onClick={() => handleRequestSort('importe')}>Monto</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'librador'} direction={orderBy === 'librador' ? order : 'asc'} onClick={() => handleRequestSort('librador')}>Librador</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'fechaEmision'} direction={orderBy === 'fechaEmision' ? order : 'asc'} onClick={() => handleRequestSort('fechaEmision')}>Emisión</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'fechaVencimiento'} direction={orderBy === 'fechaVencimiento' ? order : 'asc'} onClick={() => handleRequestSort('fechaVencimiento')}>Vencimiento</TableSortLabel></TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {chequesOrdenados.length === 0 ? (
                                                                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}><Typography variant="body2" color="text.secondary">No se encontraron cheques que coincidan con la búsqueda</Typography></TableCell></TableRow>
                                                                ) : chequesOrdenados.map((cheque) => {
                                                                    const estaSeleccionado = chequesCarteraSeleccionados.has(cheque.nroCarga);
                                                                    return (
                                                                        <TableRow key={cheque.nroCarga} hover selected={estaSeleccionado} sx={{ cursor: 'pointer', bgcolor: estaSeleccionado ? 'action.selected' : 'inherit' }} onClick={() => { const nuevoSet = new Set(chequesCarteraSeleccionados); if (estaSeleccionado) nuevoSet.delete(cheque.nroCarga); else nuevoSet.add(cheque.nroCarga); setChequesCarteraSeleccionados(nuevoSet); }}>
                                                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                                                <Checkbox checked={estaSeleccionado} onChange={(e) => { const nuevoSet = new Set(chequesCarteraSeleccionados); if (e.target.checked) nuevoSet.add(cheque.nroCarga); else nuevoSet.delete(cheque.nroCarga); setChequesCarteraSeleccionados(nuevoSet); }} />
                                                                            </TableCell>
                                                                            <TableCell>{cheque.nombreBanco}</TableCell>
                                                                            <TableCell>{cheque.nroCheque}</TableCell>
                                                                            <TableCell>{cheque.nombreSucursal} ({cheque.nroSucursal})</TableCell>
                                                                            <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>{formatCurrency(cheque.importe)}</TableCell>
                                                                            <TableCell>{cheque.librador}</TableCell>
                                                                            <TableCell>{cheque.fechaEmision ? new Date(cheque.fechaEmision).toLocaleDateString() : '-'}</TableCell>
                                                                            <TableCell>{cheque.fechaVencimiento ? new Date(cheque.fechaVencimiento).toLocaleDateString() : '-'}</TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                );
                                            }
                                            return (
                                                <Box sx={{ maxHeight: { xs: '50vh', sm: 400 }, overflowY: 'auto', pr: 1 }}>
                                                    <Grid container spacing={1.5}>
                                                        {chequesOrdenados.length === 0 ? (
                                                            <Grid item xs={12}><Typography variant="body2" color="text.secondary">No se encontraron cheques que coincidan con la búsqueda</Typography></Grid>
                                                        ) : chequesOrdenados.map((cheque) => {
                                                            const estaSeleccionado = chequesCarteraSeleccionados.has(cheque.nroCarga);
                                                            return (
                                                                <Grid item xs={12} key={cheque.nroCarga}>
                                                                    <Card variant={estaSeleccionado ? 'elevation' : 'outlined'} sx={{ borderColor: estaSeleccionado ? 'primary.main' : undefined }}>
                                                                        <CardContent>
                                                                            <FormControlLabel
                                                                                control={<Checkbox checked={estaSeleccionado} onChange={(e) => { const nuevoSet = new Set(chequesCarteraSeleccionados); if (e.target.checked) nuevoSet.add(cheque.nroCarga); else nuevoSet.delete(cheque.nroCarga); setChequesCarteraSeleccionados(nuevoSet); }} />}
                                                                                label={<Box><Typography sx={{ fontWeight: 600 }}>{cheque.nombreBanco}</Typography><Typography variant="body2">N° {cheque.nroCheque} · {formatCurrency(cheque.importe)} · {cheque.librador}</Typography></Box>}
                                                                                sx={{ m: 0, width: '100%' }}
                                                                            />
                                                                        </CardContent>
                                                                    </Card>
                                                                </Grid>
                                                            );
                                                        })}
                                                    </Grid>
                                                </Box>
                                            );
                                        })()}
                                        {chequesCarteraSeleccionados.size > 0 && (
                                            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{chequesCarteraSeleccionados.size} cheque{chequesCarteraSeleccionados.size > 1 ? 's' : ''} seleccionado{chequesCarteraSeleccionados.size > 1 ? 's' : ''}</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>Total: {formatCurrency(totalImporteSeleccionado)}</Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    );
                }
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Medio de Cobro</InputLabel>
                                <Select value={chequeSeleccionadoIdx || ''} onChange={handleChequeMedioChange} label="Medio de Cobro">
                                    {mediosCheques.map((medio, idx) => (
                                        <MenuItem key={idx} value={String(idx)}>{medio.descripcion || medio.nombre || medio.mcp || `Medio ${idx + 1}`}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Banco" value={pagoActual.banco || ''} placeholder="Seleccionar banco..." required InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><IconButton onClick={() => { setOpenBancoDialogPara('cheque'); setOpenBancoDialog(true); }} size="small"><ArrowDropDownIcon /></IconButton></InputAdornment> }} onClick={() => { setOpenBancoDialogPara('cheque'); setOpenBancoDialog(true); }} />
                        </Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="N° Sucursal" name="nroSucursal" type="number" value={pagoActual.nroSucursal || ''} onChange={handlePagoFieldChange} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="N° Cheque" name="nroCheque" value={pagoActual.nroCheque || ''} onChange={handlePagoFieldChange} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Librador" name="librador" value={pagoActual.librador || ''} onChange={handlePagoFieldChange} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="CUIT del librador" name="nroLibrador" value={pagoActual.nroLibrador || ''} onChange={handlePagoFieldChange} placeholder="Ej: 20-12345678-9" inputProps={{ maxLength: 13 }} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Fecha Emisión" name="fechaEmision" type="date" value={pagoActual.fechaEmision || ''} onChange={handlePagoFieldChange} InputLabelProps={{ shrink: true }} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Fecha Vencimiento" name="fechaVencimiento" type="date" value={pagoActual.fechaVencimiento || ''} onChange={handlePagoFieldChange} InputLabelProps={{ shrink: true }} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Importe" name="importe" type="number" value={pagoActual.importe || ''} onChange={handlePagoFieldChange} InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }} required /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Observaciones" name="detalle" value={pagoActual.detalle || ''} onChange={handlePagoFieldChange} multiline rows={2} /></Grid>
                    </Grid>
                );
            case 'Otros':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                OTROS - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <RadioGroup
                                    name="otrosOption"
                                    value={otrosSeleccionadoIdx ?? ''}
                                    onChange={(e) => {
                                        const newIdx = String(e.target.value);
                                        setOtrosSeleccionadoIdx(newIdx);
                                        const idx = Number(newIdx);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosOtros.length) {
                                            const medioSel = mediosOtros[idx];
                                            const idNum = Number(medioSel.id);
                                            setPagoActual(prev => ({ ...prev, nroCuenta: Number.isFinite(idNum) ? idNum : undefined, medioLabel: medioSel.nombre, mcp: medioSel.mcp || medioSel.nombre }));
                                        }
                                    }}
                                >
                                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                        {mediosOtros.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(otrosSeleccionadoIdx) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosOtros.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Monto"
                                name="importe"
                                type="number"
                                value={isMontoFocused && pagoActual.importe === 0 ? '' : pagoActual.importe}
                                onChange={handlePagoFieldChange}
                                onFocus={() => setIsMontoFocused(true)}
                                onBlur={() => setIsMontoFocused(false)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );

            default:
                return null;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!imputableSeleccionado) {
            enqueueSnackbar('Debe seleccionar un Motivo', { variant: 'warning' });
            return;
        }

        if (!formulario.nroSuc || formulario.nroSuc <= 0) {
            enqueueSnackbar('Debe seleccionar un N° Sucursal válido.', { variant: 'warning' });
            return;
        }

        // Validación para depósito (55) o extracción (56) bancaria: debe haber banco de cartera seleccionado.
        const tipoActual = (formulario.tipoMovimiento || tipoMovimientoInicial || COD_INGRESO).toString();
        if (origenMovimiento === 'bancario' && (tipoActual === COD_INGRESO || tipoActual === COD_EGRESO)) {
            const codigoEntidadNum = Number(formulario.codigoEntidad);
            if (!Number.isFinite(codigoEntidadNum) || codigoEntidadNum <= 0) {
                enqueueSnackbar('Debe seleccionar un banco de cartera (NroBanco ≥ 1000).', { variant: 'warning' });
                return;
            }
        }

        if (detallesPago.length === 0 || totalMedios <= 0) {
            enqueueSnackbar('Debe agregar al menos un medio de pago con monto > 0', { variant: 'warning' });
            return;
        }

        const grupoMCP = grupoCajaSeleccionado?.codigo;
        if (!grupoMCP || !Number.isFinite(grupoMCP)) {
            enqueueSnackbar('Debe seleccionar un Grupo de Caja válido', { variant: 'warning' });
            setOpenGrupoCajaDialog(true);
            return;
        }

        // Determinar tipoMovimiento según selección inicial (el backend espera STRING).
        // Para "varios":   COD_INGRESO = "5"  / COD_EGRESO = "7"
        // Para "bancario": COD_INGRESO = "55" / COD_EGRESO = "56"
        const tipoMovimiento = (formulario.tipoMovimiento || tipoMovimientoInicial || COD_INGRESO).toString();

        // Construir mediosDeCobro (en camelCase como otros endpoints de Tesorería)
        const mediosDeCobro = detallesPago.map((p) => {
            const base: any = {
                mcp: p.mcp || '',
                importe: Number(p.importe) || 0, // Asegurar que sea número, no string
                detalle: p.detalle || '',
            };

            if (p.tipoPago === 'Cheque') {
                base.cheque = {
                    nroBanco: p.nroBanco ?? 0,
                    nroSucursal: p.nroSucursal ?? 0,
                    nroCheque: p.nroCheque || '',
                    nroLibrador: p.nroLibrador || '',
                    librador: p.librador || '',
                    fechaEmision: p.fechaEmision ? new Date(p.fechaEmision).toISOString() : new Date().toISOString(),
                    fechaVencimiento: p.fechaVencimiento ? new Date(p.fechaVencimiento).toISOString() : new Date().toISOString(),
                    ctaBancaria: p.ctaBancaria || '',
                    concepto: p.concepto || '',
                    ...(p.nroCarga ? { nroCarga: p.nroCarga } : {}),
                };
            }

            if (p.tipoPago === 'Tarjeta') {
                // Usar los datos del medio seleccionado (obtenidos cuando se seleccionó el medio)
                base.tarjeta = {
                    codigoMarca: Number(p.codigoMarca) || 0,
                    nroTarjeta: p.nroTarjeta || '',
                    nroComprobanteTa: Number(p.nroComprobanteTa) || 0,
                    fecha: p.fecha ? new Date(p.fecha).toISOString() : new Date().toISOString(),
                    fechaPresentar: p.fechaPresentar ? new Date(p.fechaPresentar).toISOString() : new Date().toISOString(),
                    numeroroAutorizacion: p.numeroroAutorizacion || '',
                    telefono: p.telefono || '',
                    titularTarjeta: p.titularTarjeta || ''
                };
            }

            return base;
        });

        // Validar que nroSuc esté correctamente establecido
        const nroSucEnviar = formulario.nroSuc;
        console.log('🔍 Validación antes de enviar:');
        console.log('  - formulario.nroSuc:', formulario.nroSuc);
        console.log('  - tipo:', typeof formulario.nroSuc);
        console.log('  - es válido (> 0):', nroSucEnviar > 0);

        if (!nroSucEnviar || nroSucEnviar <= 0) {
            enqueueSnackbar('Debe seleccionar un N° Sucursal válido antes de guardar', { variant: 'error' });
            return;
        }

        const payload = {
            fecha: formulario.fechaEmision ? new Date(formulario.fechaEmision).toISOString() : new Date().toISOString(),
            grupoMCP: grupoMCP,
            nroSuc: nroSucEnviar,
            tipoMovimiento: tipoMovimiento,
            codigoEntidad: formulario.codigoEntidad,
            descripcion: formulario.descripcion,
            aclaracion: formulario.aclaracion,
            mediosDeCobro: mediosDeCobro,
        };

        console.log('📤 Enviando movimiento de caja (directo):', payload);
        console.log('📤 Payload completo:', JSON.stringify(payload, null, 2));
        console.log('📤 nroSuc en payload:', payload.nroSuc, '(tipo:', typeof payload.nroSuc, ')');
        console.log('📤 grupoMCP en payload:', payload.grupoMCP, '(tipo:', typeof payload.grupoMCP, ')');
        console.log('📤 tipoMovimiento en payload:', payload.tipoMovimiento, '(tipo:', typeof payload.tipoMovimiento, ')');
        console.log('📤 mediosDeCobro en payload:', payload.mediosDeCobro?.length || 0, 'elementos');
        console.log('📤 Primer medio de cobro:', payload.mediosDeCobro?.[0]);
        console.log('📤 Importe del primer medio:', payload.mediosDeCobro?.[0]?.importe, '(tipo:', typeof payload.mediosDeCobro?.[0]?.importe, ')');

        try {
            await api.post('/api/TesoreriaCaja/movimientos-caja', payload);
            enqueueSnackbar('Movimiento creado exitosamente', { variant: 'success' });
            navigate(origenMovimiento === 'bancario' ? '/tesoreria/movimientos-bancarios' : '/tesoreria/movimientos-varios');
        } catch (error: any) {
            console.error('❌ Error al guardar movimiento:', error);

            let mensaje = 'Error al guardar el movimiento';
            const data = error?.response?.data;

            if (data) {
                if (typeof data === 'string') {
                    mensaje = data;
                } else if (data.message) {
                    mensaje = data.message;
                } else if (data.title) {
                    mensaje = data.title;
                }

                // Extraer el primer mensaje de validación si existe data.errors (típico de ASP.NET)
                if (data.errors && typeof data.errors === 'object') {
                    const firstKey = Object.keys(data.errors)[0];
                    const firstError = (data.errors as any)[firstKey]?.[0];
                    if (firstError) {
                        mensaje = `${firstKey}: ${firstError}`;
                    }
                }

                try {
                    console.log('🔍 Detalle de error de validación movimientos-caja (JSON):', JSON.stringify(data, null, 2));
                } catch {
                    console.error('🔍 Detalle de error de validación movimientos-caja:', data);
                }
            }

            enqueueSnackbar(mensaje, { variant: 'error' });
        }
    };

    return (
        <Box sx={{ position: 'relative', p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
            <Card
                sx={{
                    bgcolor: '#ffffff',
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography
                        variant={isSmallScreen ? "h5" : "h4"}
                        sx={{
                            mb: { xs: 2, sm: 3 },
                            fontWeight: 600,
                            color: '#1976d2',
                            fontSize: { xs: '1.5rem', sm: '2rem' }
                        }}
                    >
                        Nuevo Movimiento
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Card sx={{ mb: 2 }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Grid container spacing={{ xs: 2, sm: 3 }}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Motivo"
                                            value={imputableSeleccionado ? `${imputableSeleccionado.nroCuenta} - ${imputableSeleccionado.nomCuenta || ''}` : ''}
                                            required
                                            helperText="Escriba el número para buscar automáticamente"
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => setOpenImputableSearch(true)}
                                                            edge="end"
                                                            color="primary"
                                                            title="Seleccionar Motivo"
                                                            size={isSmallScreen ? "small" : "medium"}
                                                        >
                                                            <ArrowDropDownIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth required>
                                            <InputLabel>Nº Sucursal</InputLabel>
                                            <Select<number>
                                                value={formulario.nroSuc || 0}
                                                onChange={handleSucursalChange}
                                                label="Nº Sucursal"
                                                disabled={loadingSucursales}
                                            >
                                                {sucursales.map((sucursal) => (
                                                    <MenuItem key={sucursal.nroSuc} value={sucursal.nroSuc}>
                                                        {sucursal.nroSuc} - {sucursal.nombreSucursal?.trim() || `Sucursal ${sucursal.nroSuc}`}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Fecha"
                                            name="fechaEmision"
                                            type="date"
                                            value={formulario.fechaEmision}
                                            onChange={handleFormularioChange}
                                            InputLabelProps={{ shrink: true }}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Tipo Movimiento"
                                            value={formulario.tipoMovimiento === COD_EGRESO ? LABEL_EGRESO : LABEL_INGRESO}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>
                                    {origenMovimiento === 'bancario' && ((formulario.tipoMovimiento || tipoMovimientoInicial || COD_INGRESO) === COD_INGRESO || (formulario.tipoMovimiento || tipoMovimientoInicial) === COD_EGRESO) && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label={(formulario.tipoMovimiento || tipoMovimientoInicial) === COD_EGRESO ? 'Banco (Extracción Bancaria)' : 'Banco (Depósito Bancario)'}
                                                value={
                                                    bancoDepositoSeleccionado
                                                        ? `${bancoDepositoSeleccionado.nombre}${bancoDepositoSeleccionado.nroBanco ? ` (${bancoDepositoSeleccionado.nroBanco})` : ''}`
                                                        : (formulario.codigoEntidad
                                                            ? `Código entidad: ${formulario.codigoEntidad}`
                                                            : '')
                                                }
                                                required
                                                helperText="Seleccione un banco de cartera (NroBanco ≥ 1000)"
                                                InputProps={{
                                                    readOnly: true,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => { setOpenBancoDialogPara('codigoEntidad'); setBancoSeleccionadoId(null); setOpenBancoDialog(true); }}
                                                                edge="end"
                                                                color="primary"
                                                                size={isSmallScreen ? "small" : "medium"}
                                                            >
                                                                <ArrowDropDownIcon />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                onClick={() => { setOpenBancoDialogPara('codigoEntidad'); setBancoSeleccionadoId(null); setOpenBancoDialog(true); }}
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Observaciones"
                                            name="aclaracion"
                                            value={formulario.aclaracion}
                                            onChange={handleFormularioChange}
                                            multiline
                                            rows={2}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <NotesIcon />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Sección con Solapas: Solo Medios de Pago */}
                        <Box sx={{ mb: 1 }}>
                            <Card sx={{ boxShadow: 2 }}>
                                <Box sx={{ position: 'relative', borderBottom: 1, borderColor: 'divider', minHeight: { xs: 48, sm: 56 }, px: { xs: 1, sm: 7 } }}>
                                    <Tabs
                                        value={tabValue}
                                        onChange={(e, newValue) => setTabValue(newValue)}
                                        aria-label="solapas de Movimiento"
                                        variant={isSmallScreen ? "scrollable" : "standard"}
                                        scrollButtons="auto"
                                        sx={{
                                            '& .MuiTab-root': {
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                minHeight: { xs: 48, sm: 56 },
                                                padding: { xs: '8px 12px', sm: '12px 16px' }
                                            }
                                        }}
                                    >
                                        <Tab label="Medios de Pago" />
                                    </Tabs>
                                </Box>

                                <TabPanel value={tabValue} index={0}>
                                    <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 1 }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<AddIcon />}
                                                    onClick={handleOpenPagoDialog}
                                                    disabled={!imputableSeleccionado}
                                                    fullWidth={isSmallScreen}
                                                    sx={{
                                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                                        py: { xs: 1.5, sm: 1 }
                                                    }}
                                                >
                                                    Agregar Pago
                                                </Button>
                                            </Box>

                                            <Grid container spacing={2}>
                                                {detallesPago.map((pago, index) => (
                                                    <Grid item xs={12} key={index}>
                                                        <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 0 }}>
                                                            <CardContent>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                    <Box>
                                                                        <Typography sx={{ fontWeight: 800, color: 'primary.main' }}>
                                                                            {pago.tipoPago?.toUpperCase()}
                                                                        </Typography>
                                                                        {!!pago.detalle && (
                                                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                                                                {pago.detalle}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Typography sx={{ fontWeight: 800, color: 'success.main' }}>
                                                                            {formatCurrency(pago.importe || 0)}
                                                                        </Typography>
                                                                        <IconButton
                                                                            onClick={() => removePago(index)}
                                                                            size="small"
                                                                            sx={{ color: 'text.secondary' }}
                                                                        >
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    </Box>
                                                                </Box>
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                ))}
                                                {detallesPago.length === 0 && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="body2">Aún no has agregado medios de pago.</Typography>
                                                    </Grid>
                                                )}
                                            </Grid>

                                            <Box sx={{ mt: 2, p: 2, borderRadius: 2, borderLeft: '6px solid', borderColor: 'primary.main', bgcolor: 'background.paper' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography color="text.secondary">Total Medios:</Typography>
                                                    <Typography sx={{ color: 'primary.main', fontWeight: 800 }}>{formatCurrency(totalMedios)}</Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{ mt: 2 }}>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    color="primary"
                                                    fullWidth
                                                    disabled={totalMedios <= 0}
                                                    sx={{
                                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                                        py: { xs: 1.5, sm: 1 }
                                                    }}
                                                >
                                                    Procesar Movimiento
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </TabPanel>
                            </Card>
                        </Box>
                    </form>
                </CardContent>
            </Card>

            {/* Dialog para buscar Imputable (Motivo) */}
                    <ImputableSearch
                        open={openImputableSearch}
                        onClose={() => setOpenImputableSearch(false)}
                        onSelect={handleImputableSelect}
                        tipoMovimientoIE={
                            (formulario.tipoMovimiento || tipoMovimientoInicial) === COD_EGRESO
                                ? 'E'
                                : 'I'
                        }
                    />

            {/* Dialog para agregar/editar pagos */}
            <Dialog
                open={openPagoDialog}
                onClose={handleClosePagoDialog}
                maxWidth={esChequeCartera ? 'xl' : 'sm'}
                fullWidth
                fullScreen={isSmallScreen}
            >
                <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, pb: { xs: 1, sm: 2 } }}>
                    Agregar Pago
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                    <Box sx={{ mt: { xs: 1, sm: 2 } }}>
                        <Stepper activeStep={activeStep} sx={{ mb: { xs: 3, sm: 4 } }}>
                            <Step>
                                <StepLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Seleccionar Tipo</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Completar Datos</StepLabel>
                            </Step>
                        </Stepper>

                        {activeStep === 0 ? (
                            <FormControl fullWidth>
                                <InputLabel>Tipo de Pago</InputLabel>
                                <Select
                                    value={tipoPagoSeleccionado}
                                    onChange={handleTipoPagoChange}
                                    label="Tipo de Pago"
                                >
                                    <MenuItem value="Efectivo">Efectivo</MenuItem>
                                    <MenuItem value="Transferencia">Transferencias</MenuItem>
                                    <MenuItem value="Cheque">Cheques de Tercero</MenuItem>
                                    <MenuItem value="Tarjeta">Tarjetas</MenuItem>
                                    <MenuItem value="Otros">Otros</MenuItem>
                                </Select>
                            </FormControl>
                        ) : (
                            renderCamposPago()
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
                    <Button onClick={handleClosePagoDialog}>
                        Cancelar
                    </Button>
                    {activeStep > 0 && (
                        <Button onClick={() => setActiveStep(0)}>
                            Atrás
                        </Button>
                    )}
                    {activeStep === 1 && (
                        <Button
                            onClick={handleAgregarPago}
                            variant="contained"
                            color="primary"
                            disabled={esChequeCartera && chequesCarteraSeleccionados.size === 0}
                        >
                            Agregar
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Diálogo de selección de Banco (igual que en carga de recibos) */}
            <Dialog
                open={openBancoDialog}
                onClose={() => { setOpenBancoDialog(false); setOpenBancoDialogPara(null); }}
                maxWidth="sm"
                fullWidth
                fullScreen={isSmallScreen}
            >
                <DialogTitle>Seleccionar Banco</DialogTitle>
                <DialogContent>
                    {openBancoDialogPara === 'codigoEntidad' && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Solo se muestran bancos de cartera (NroBanco ≥ 1000).
                        </Typography>
                    )}
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            placeholder="Buscar banco..."
                            value={bancoSearch}
                            onChange={(e) => setBancoSearch(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                    <Box sx={{ maxHeight: { xs: '60vh', sm: 360 }, overflowY: 'auto', pr: 1 }} onScroll={handleBancoScroll}>
                        <Grid container spacing={1}>
                            {(() => {
                                const bancosParaMostrar = openBancoDialogPara === 'codigoEntidad'
                                    ? bancos.filter((b) => {
                                        const n = Number(b.nroBanco ?? b.codigo);
                                        return Number.isFinite(n) && n >= 1000;
                                    })
                                    : bancos;
                                return (
                                    <>
                                        {bancosParaMostrar.map((banco, idx) => (
                                            <Grid item xs={12} key={banco.id ?? idx}>
                                                <Card
                                                    variant={bancoSeleccionadoId === banco.id ? 'elevation' : 'outlined'}
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => setBancoSeleccionadoId(banco.id)}
                                                >
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <input
                                                            type="radio"
                                                            checked={bancoSeleccionadoId === banco.id}
                                                            onChange={() => setBancoSeleccionadoId(banco.id)}
                                                            style={{ marginRight: 8 }}
                                                        />
                                                        <Typography sx={{ fontWeight: 600 }}>
                                                            {`${banco.nombre}${banco.nroBanco ? ` (${banco.nroBanco})` : ''}`}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {!bancoLoading && bancosParaMostrar.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">
                                                    {openBancoDialogPara === 'codigoEntidad'
                                                        ? 'No hay bancos de cartera (NroBanco ≥ 1000) en la lista. Cargue más páginas o busque otro término.'
                                                        : 'No se encontraron bancos.'}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </>
                                );
                            })()}
                            {bancoLoading && (
                                <Grid item xs={12}>
                                    <Typography variant="body2">Cargando bancos...</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenBancoDialog(false); setOpenBancoDialogPara(null); }}>Cancelar</Button>
                    <Button
                        variant="contained"
                        disabled={bancoSeleccionadoId == null}
                        onClick={() => {
                            const banco = bancos.find(b => b.id === bancoSeleccionadoId);
                            if (banco) {
                                const label = `${banco.nombre}${banco.nroBanco ? ` (${banco.nroBanco})` : ''}`;
                                const nroBancoNum = Number(banco.nroBanco);
                                const nroBancoFinal = Number.isFinite(nroBancoNum)
                                    ? nroBancoNum
                                    : (typeof banco.codigo === 'number'
                                        ? banco.codigo
                                        : Number(banco.codigo));
                                setPagoActual(prev => ({
                                    ...prev,
                                    banco: label,
                                    nroBanco: Number.isFinite(nroBancoFinal) ? nroBancoFinal : banco.id,
                                }));

                                // Depósito (55) o Extracción (56) bancario: CodigoEntidad = NroBanco del banco de cartera (>= 1000).
                                const tipoActual = (formulario.tipoMovimiento || tipoMovimientoInicial || COD_INGRESO).toString();
                                if (openBancoDialogPara === 'codigoEntidad' && origenMovimiento === 'bancario' && (tipoActual === COD_INGRESO || tipoActual === COD_EGRESO) && Number.isFinite(nroBancoFinal)) {
                                    setBancoDepositoSeleccionado(banco);
                                    setFormulario(prev => ({
                                        ...prev,
                                        codigoEntidad: nroBancoFinal,
                                    }));
                                }
                            }
                            setOpenBancoDialog(false);
                            setOpenBancoDialogPara(null);
                        }}
                    >
                        Seleccionar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para seleccionar Grupo de Caja */}
            <Dialog
                open={openGrupoCajaDialog}
                onClose={() => setOpenGrupoCajaDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Seleccionar Grupo de Caja</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        {grupoCajaLoading ? (
                            <Typography>Cargando grupos...</Typography>
                        ) : (
                            <FormControl fullWidth>
                                <InputLabel>Grupo de Caja</InputLabel>
                                <Select
                                    value={grupoCajaSeleccionado?.id || ''}
                                    onChange={(e) => {
                                        const grupo = gruposCaja.find(g => g.id === Number(e.target.value));
                                        setGrupoCajaSeleccionado(grupo || null);
                                    }}
                                    label="Grupo de Caja"
                                >
                                    {gruposCaja.map((grupo) => (
                                        <MenuItem key={grupo.id} value={grupo.id}>
                                            {grupo.nombre} ({grupo.codigo})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenGrupoCajaDialog(false)}>Cancelar</Button>
                    <Button
                        onClick={() => {
                            if (grupoCajaSeleccionado) {
                                // Cerrar selección de grupo y forzar inmediatamente la selección de forma de pago
                                setOpenGrupoCajaDialog(false);
                                setTimeout(() => {
                                    handleOpenPagoDialog();
                                }, 0);
                            }
                        }}
                        variant="contained"
                        disabled={!grupoCajaSeleccionado}
                    >
                        Seleccionar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Overlay inicial para elegir Tipo de Movimiento */}
            {!tipoMovimientoConfirmado && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1300,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        pt: { xs: 6, sm: 10 },
                        backdropFilter: 'blur(4px)',
                        backgroundColor: 'rgba(255,255,255,0.6)',
                    }}
                >
                    <Card
                        sx={{
                            bgcolor: '#ffffff',
                            borderRadius: 3,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                            maxWidth: 600,
                            width: '100%',
                            mx: 2,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Typography
                                variant={isSmallScreen ? "h5" : "h4"}
                                sx={{
                                    mb: { xs: 2, sm: 3 },
                                    fontWeight: 600,
                                    color: '#1976d2',
                                    fontSize: { xs: '1.5rem', sm: '2rem' }
                                }}
                            >
                                Nuevo Movimiento
                            </Typography>

                            <Grid container spacing={{ xs: 2, sm: 3 }}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Tipo Movimiento</InputLabel>
                                        <Select
                                            value={tipoMovimientoInicial}
                                            label="Tipo Movimiento"
                                            onChange={(e) => setTipoMovimientoInicial(e.target.value as string)}
                                        >
                                            <MenuItem value={COD_INGRESO}>{LABEL_INGRESO}</MenuItem>
                                            <MenuItem value={COD_EGRESO}>{LABEL_EGRESO}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                            onClick={() => navigate(origenMovimiento === 'bancario' ? '/tesoreria/movimientos-bancarios' : '/tesoreria/movimientos-varios')}
                                    sx={{ textTransform: 'none', borderRadius: 2, px: 3, py: 1 }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={!tipoMovimientoInicial}
                                    onClick={() => {
                                        setFormulario(prev => ({
                                            ...prev,
                                                    tipoMovimiento: tipoMovimientoInicial || COD_INGRESO,
                                        }));
                                        setTipoMovimientoConfirmado(true);
                                    }}
                                    sx={{ textTransform: 'none', borderRadius: 2, px: 3, py: 1 }}
                                >
                                    Continuar
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            )}
        </Box>
    );
};

export default NuevoMovimientoCaja;
